import { useEffect, useRef, useState } from 'react';
import { boreholesApi } from '../lib/api';
import { boreholePaint, boreholesToGeoJSON } from '../lib/boreholes';
import { featureBounds } from './useDatasetRawLayers';

/**
 * Borehole collars as a map layer (docs/BOREHOLES.md):
 *   /map?boreholes=<datasetId>  the holes imported from that dataset (and zoom to them)
 *   /map?project=<projectId>    every hole in the project
 * The layer goes into MapView's resultLayers like the dataset raw layers, so it renders,
 * toggles and removes the same way; the click popup comes from `dataset.kind === 'boreholes'`
 * (useDatasetRawLayers.rawPopupProps).
 */

/** The result-layer entry for a set of boreholes, or null when none has a location. */
export function buildBoreholeLayer(key, label, boreholes) {
  const geojson = boreholesToGeoJSON(boreholes);
  if (!geojson.features.length) return null;
  return {
    id: `boreholes-${key}`, name: `Boreholes — ${label}`, geojson, kind: 'boreholes', visible: true,
    paint: boreholePaint(), dataset: { id: key, kind: 'boreholes', url: '/api/boreholes' },
  };
}

/** status: 'idle' | 'loading' | 'ready' | 'empty' | 'error' */
export function useBoreholeLayers({ searchParams, setResultLayers, mapRefHolder }) {
  const datasetId = searchParams.get('boreholes');
  const projectId = searchParams.get('project');
  const [status, setStatus] = useState('idle');
  const [count, setCount] = useState(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!datasetId && !projectId) { setStatus('idle'); return undefined; }
    let active = true;
    const key = datasetId || projectId;
    const params = { include_intervals: true, limit: 5000, ...(datasetId ? { dataset_id: datasetId } : { project_id: projectId }) };
    setStatus('loading');
    boreholesApi.list(params)
      .then((res) => {
        if (!active) return;
        const holes = res.data?.boreholes || [];
        const layer = buildBoreholeLayer(key, datasetId ? 'dataset' : 'project', holes);
        setCount(holes.length);
        if (!layer) { setStatus('empty'); return; }
        setResultLayers(prev => [...prev.filter(r => r.id !== layer.id), layer]);
        setStatus('ready');
        if (datasetId) {
          const bounds = featureBounds([layer.geojson]);
          let tries = 0;
          const t = setInterval(() => {
            const map = mapRefHolder?.current?.current?.getMap?.();
            if (map && bounds) {
              clearInterval(t);
              if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) map.jumpTo({ center: bounds[0], zoom: 16 });
              else map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 18 });
            } else if (++tries > 40 || !mounted.current) clearInterval(t);
          }, 150);
        }
      })
      .catch((e) => {
        // 503 before migration 028, or no access: the map works without the layer
        if (active) { setStatus('error'); console.warn('Boreholes not loaded:', e?.response?.data?.detail || e?.message); }
      });
    return () => { active = false; };
  }, [datasetId, projectId, setResultLayers, mapRefHolder]);

  return { status, count };
}
